/* eslint @typescript-eslint/strict-boolean-expressions: off */

import { intersectionBy } from "lodash";
import React from "react";
import { VisualizationWidget } from "lib/widgets/types";
import { isQueryVisual } from "lib/visuals/visuals";
import { ActiveContext } from "shared/ContextSwitcher/store";
import { useGetAvailablePerformanceTiers } from "./useGetAvailablePerformanceTiers";
import {
  ExecutionTiersData,
  SupportedPublicPerformanceTierKey,
} from "shared/SelectPerformance/useGetPerformanceTiers";
import { getDefaultPerformanceKey } from "shared/SelectPerformance/utils/getDefaultPerformanceKey";
import { useGetIsDatasetIdExecutable } from "lib/datasets/useGetIsDatasetIdExecutable";

interface DashboardArguments {
  queryIds: number[];
  unexecutableQueryIds: number[];
  performance: SupportedPublicPerformanceTierKey | undefined;
  selectedPerformanceTier: ExecutionTiersData | undefined;
  performanceTiers: ExecutionTiersData[];
  setPerformance: React.Dispatch<
    React.SetStateAction<SupportedPublicPerformanceTierKey | undefined>
  >;
}

export const useGetRefreshDashboardArguments = (
  vizWidgets: VisualizationWidget[],
  activeContext: ActiveContext | undefined
): DashboardArguments => {
  const getIsDatasetIdExecutable = useGetIsDatasetIdExecutable(activeContext);

  const getAvailablePerformanceTiers = useGetAvailablePerformanceTiers(
    activeContext
  );
  const [selectedPerformance, setActivePerformance] = React.useState<
    SupportedPublicPerformanceTierKey | undefined
  >();

  let selectedPerformanceTier: ExecutionTiersData | undefined;
  let availablePerformanceTiers: ExecutionTiersData[] | undefined;
  const queryIds = new Set<number>();
  const unexecutableQueryIds = new Set<number>();
  const datasetIds = new Set<number>();

  for (const w of vizWidgets) {
    if (isQueryVisual(w.visualization)) {
      const queryDetails = w.visualization.query_details;

      if (queryDetails) {
        if (getIsDatasetIdExecutable(queryDetails.dataset_id)) {
          queryIds.add(queryDetails.query_id);
          datasetIds.add(queryDetails.dataset_id);
        } else {
          unexecutableQueryIds.add(queryDetails.query_id);
        }
      }
    }
  }

  //TODO remove condition after migration to v2 is complete
  if (activeContext?.serviceTier.version === "v2") {
    datasetIds.forEach((datasetId) => {
      if (!availablePerformanceTiers) {
        availablePerformanceTiers = getAvailablePerformanceTiers(
          datasetId
        ).filter(({ credits }) => credits > 0);
      } else {
        availablePerformanceTiers = intersectionBy(
          availablePerformanceTiers,
          getAvailablePerformanceTiers(datasetId),
          "publicPerformanceTierKey"
        );
      }
    });

    if (availablePerformanceTiers) {
      // Find the first non-free performance tier
      const defaultPerformance = getDefaultPerformanceKey(
        activeContext,
        availablePerformanceTiers
      );

      // set default performance if no performance is selected
      if (!selectedPerformance && defaultPerformance) {
        setActivePerformance(defaultPerformance);
      }

      if (selectedPerformance) {
        selectedPerformanceTier = availablePerformanceTiers.find(
          ({ publicPerformanceTierKey }) =>
            publicPerformanceTierKey === selectedPerformance
        );
      }
    }
  }

  return {
    queryIds: [...queryIds],
    unexecutableQueryIds: [...unexecutableQueryIds],
    performance: selectedPerformance,
    setPerformance: setActivePerformance,
    selectedPerformanceTier: selectedPerformanceTier,
    performanceTiers: availablePerformanceTiers || [],
  };
};
